import { Features } from "@storytell/cp-client";
import { createSignal } from "solid-js";
import { useAppUI } from "~/components/layouts/minimal/AppUIContext";
import type { KnowledgeSourcesState } from "../../PromptBar.types";

/**
 * Hook for managing knowledge sources state and interactions
 * Uses layout context for persistence across SSR
 */
export const useKnowledgeSourcesDisplay = () => {
  const { state, actions } = useAppUI();
  const [showTooltip, setShowTooltip] = createSignal(false);
  const [selectedModel, setSelectedModel] = createSignal<Features.Feature>(
    Features.Feature.MODEL_STORYTELL_AUTO_SELECT,
  );

  const toggleTooltip = () => setShowTooltip(!showTooltip());
  const hideTooltip = () => setShowTooltip(false);

  const toggleTraining = () => actions.toggleKnowledgeSource("training");
  const toggleWeb = () => actions.toggleKnowledgeSource("web");
  const toggleMyKnowledge = () => actions.toggleKnowledgeSource("myKnowledge");
  const toggleMemories = () => actions.toggleKnowledgeSource("memories");

  const knowledgeState: KnowledgeSourcesState & {
    setShowTooltip: (show: boolean) => void;
    toggleTooltip: () => void;
    hideTooltip: () => void;
    toggleTraining: () => void;
    toggleWeb: () => void;
    toggleMyKnowledge: () => void;
    toggleMemories: () => void;
    setSelectedModel: (model: Features.Feature) => void;
  } = {
    // Use layout context state for persistence
    training: () => state.promptBar.knowledgeSources.training,
    web: () => state.promptBar.knowledgeSources.web,
    myKnowledge: () => state.promptBar.knowledgeSources.myKnowledge,
    memories: () => state.promptBar.knowledgeSources.memories,
    // Local state for UI-only features
    showTooltip,
    selectedModel,
    setShowTooltip,
    toggleTooltip,
    hideTooltip,
    toggleTraining,
    toggleWeb,
    toggleMyKnowledge,
    toggleMemories,
    setSelectedModel,
  };

  return knowledgeState;
};
